/**
 * Ramp Embed - Type Definitions
 */

// =============================================================================
// Configuration
// =============================================================================

export interface EmbedConfig {
  apiBaseUrl: string;
  stripePublishableKey: string;
  stripePublishableKeyUK: string;
  stripePublishableKeyEU: string;
  stripePublishableKeyStablecoin: string;
  isProduction: boolean;
}

// =============================================================================
// API Enums
// =============================================================================

export type IssuerType = "MARQETA" | "STRIPE";

export type IssuingProduct =
  | "MQ_CAD"
  | "MQ_COLUMN_MNP_AUD"
  | "MQ_COLUMN_MNP_BRL"
  | "MQ_COLUMN_MNP_JPY"
  | "MQ_COLUMN_MNP_MXN"
  | "MQ_COLUMN_MNP_SGD"
  | "MQ_LEAD_USA"
  | "MQ_PTC_CA_USD"
  | "MQ_USA"
  | "STRIPE_BG"
  | "STRIPE_DE"
  | "STRIPE_ES"
  | "STRIPE_FI"
  | "STRIPE_FIFTH_THIRD_GOV_USA"
  | "STRIPE_FR"
  | "STRIPE_GOV_NON_FED_USA"
  | "STRIPE_HU"
  | "STRIPE_IE"
  | "STRIPE_IT"
  | "STRIPE_NL"
  | "STRIPE_PL"
  | "STRIPE_RO"
  | "STRIPE_SI"
  | "STRIPE_UK"
  | "STRIPE_UK_EUR"
  | "STRIPE_UK_USD"
  | "STRIPE_USA"
  | "STRIPE_XBI_MX_USD";

// =============================================================================
// API Request/Response Types
// =============================================================================

export interface ExchangeInitTokenRequest {
  embed_init_token: string;
  parent_origin: string;
}

export interface ExchangeInitTokenResponse {
  session_token: string;
  expires_at: string;
  card_issuer: IssuerType;
  issuing_product: IssuingProduct;
  stripe_card_id: string | null;
  stripe_account: string | null;
}

export interface ExchangeSessionTokenRequest {
  embed_session_token: string;
  nonce: string | null;
}

export interface ExchangeSessionTokenResponse {
  card_id: string;
  card_issuer: IssuerType;
  is_physical: boolean;
  issuer_token: string;
  issuing_product: IssuingProduct;
  pin_is_set: boolean;
  stripe_account: string | null;
  stripe_card_id: string | null;
}

// =============================================================================
// JWT Payload
// =============================================================================

export interface EmbedInitTokenPayload {
  iss: "ramp";
  aud: "ramp:embed:exchange";
  sub: string;
  jti: string;
  exp: number;
  iat: number;
  business_id: string;
  parent_origin: string;
  scopes: string[];
}

// =============================================================================
// PostMessage Protocol
// =============================================================================

export interface EmbedInitMessage {
  type: "ramp.embed.init";
  token: string;
  v: 1;
}

export type EmbedEvent =
  | { type: "ramp.embed.ready" }
  | { type: "ramp.embed.error"; code: EmbedErrorCode; message: string }
  | { type: "ramp.embed.copied"; field: "number" | "expiry" | "cvv" | "pin" };

// =============================================================================
// Errors
// =============================================================================

export type EmbedErrorCode =
  | "INVALID_TOKEN"
  | "TOKEN_EXPIRED"
  | "ORIGIN_MISMATCH"
  | "EXCHANGE_FAILED"
  | "ISSUER_TOKEN_FAILED"
  | "CARD_DISPLAY_FAILED"
  | "NONCE_FAILED"
  | "NETWORK_ERROR"
  | "UNKNOWN";

export class EmbedError extends Error {
  constructor(
    public readonly code: EmbedErrorCode,
    message: string,
    public readonly recoverable = false
  ) {
    super(message);
    this.name = "EmbedError";
  }
}

// =============================================================================
// State
// =============================================================================

export type EmbedState =
  | "idle"
  | "exchanging"
  | "loading_card"
  | "displaying"
  | "error";
